import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router";
import CasinoHeading from "../../casinoComponents/casinoHeading";
import VideoBox from "../../casinoComponents/videoBox";
import MyBet from "../../casinoComponents/myBet";
import CasinoTableLayout from "../../casinoComponents/casinoTableLayout";
import CasinoLastResult from "../../casinoComponents/casinoLastResult";
import UserSiteLastResult from "../../casinoComponents/userSiteLastResult";
import { joinSlots } from "../../casinoComponents/userSiteLastResult/refUtils";
import RefCardsOverlay from "../../casinoComponents/videoBox/overlays/RefCardsOverlay";
import Cmatch20ResultBody from "../../casinoComponents/refResultModal/Cmatch20ResultBody";
import CmeterResultBody from "../../casinoComponents/refResultModal/CmeterResultBody";
import Cmeter1ResultBody from "../../casinoComponents/refResultModal/Cmeter1ResultBody";
import ViewMoreModal from "../../../components/ViewMoreModal";
import {
  getCasinoGameDetails,
  getMatchExposure,
  getCasinoOpenBets,
} from "../../../apiservices/CasionApi";
import { extractCasinoGame } from "../../../utils/casinoGame";
import Cmatch20Table from "./Cmatch20Table";
import CmeterTable from "./CmeterTable";
import Cmeter1Table from "./Cmeter1Table";
import "../../../styles/games/btabledtl.css";

const BALL = (n) => `${import.meta.env.BASE_URL}assets/img/balls/cricket20/ball${n}.png`;

/**
 * Per game: reference title, .casino-table classes, bet area, video card overlay,
 * Last Result item (reference LastResultsSection) and result-modal body.
 */
const GAMES = {
  cmatch20: {
    title: "Cricket Match 20-20",
    tableClass: "cricket20 detail-page-container",
    Table: Cmatch20Table,
    overlay: (card) => <RefCardsOverlay card={card} count={1} />,
    // Reference renders .casino-detail once the feed has a round
    showDetail: (raw) => !!raw?.mid,
    item: (win) => ({ className: "cricket20lastresult", content: <img src={BALL(win)} alt="" /> }),
    Body: Cmatch20ResultBody,
  },
  cmeter: {
    title: "Casino Meter",
    tableClass: "cmeter detail-page-container",
    Table: CmeterTable,
    overlay: () => null,
    // Reference: mainData.length > 0 && card != ""
    showDetail: (raw) => Array.isArray(raw?.sub) && raw.sub.length > 0 && raw?.card != null && raw.card !== "",
    item: (win) => ({ className: "resultb", content: joinSlots(win, [[1, "L"], [2, "H"], [0, "T"]]) }),
    Body: CmeterResultBody,
  },
  cmeter1: {
    title: "1 Card Meter",
    tableClass: "one-card-meter",
    videoTitle: true,
    Table: Cmeter1Table,
    overlay: (card) => <RefCardsOverlay card={card} count={2} wrap />,
    showDetail: () => true,
    item: (win) => ({
      className: win === "1" ? "resulta" : win === "2" ? "resultb" : win === "3" ? "resulttie" : undefined,
      content: joinSlots(win, [[1, "A"], [2, "B"], [3, "T"]]),
    }),
    Body: Cmeter1ResultBody,
  },
};

// Sport Casino games (Cricket Match 20-20, Casino Meter, 1 Card Meter) in the reference shell.
// App.jsx still passes the old Table / formatResult / titleOnVideo props; the page now picks
// everything per game from GAMES (reference markup), keeping only fallbackTitle.
export default function SimpleCasinoGame({ fallbackTitle = "" }) {
  const { pathname } = useLocation();
  const gameName = extractCasinoGame(pathname) || "";
  const [gameData, setGameData] = useState({});
  const [exposures, setExposures] = useState({});
  const [myBets, setMyBets] = useState([]);
  const [showViewMoreModal, setShowViewMoreModal] = useState(false);

  const raw = gameData?.data?.data || {};
  const matchId = raw?.mid ? String(raw.mid) : "";

  useEffect(() => {
    if (!gameName) return undefined;
    let cancelled = false;
    const fetchGameData = async () => {
      try {
        const response = await getCasinoGameDetails(gameName);
        if (!cancelled && response) setGameData(response);
      } catch (err) {
        console.error(`Error fetching ${gameName} game data:`, err);
      }
    };
    fetchGameData();
    const interval = setInterval(fetchGameData, 1500);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [gameName]);

  const fetchExposure = useCallback(async () => {
    if (!matchId) return;
    try {
      const response = await getMatchExposure(null, matchId);
      if (response?.success && Array.isArray(response.data)) {
        const map = {};
        response.data.forEach((item) => {
          const name = String(item.team_name || "").trim();
          const amount = parseFloat(item.exposure_amount) || 0;
          if (name && amount !== 0) {
            map[name] = (map[name] || 0) + amount;
            map[name.toLowerCase()] = map[name];
          }
        });
        setExposures(map);
      }
    } catch (error) {
      console.error("Error fetching exposure:", error);
    }
  }, [matchId]);

  const fetchMyBetsData = useCallback(async () => {
    if (!gameName) return;
    try {
      const response = await getCasinoOpenBets(gameName);
      if (response?.success && Array.isArray(response.data)) {
        setMyBets(
          response.data.map((bet) => ({
            description: bet.description ?? bet.game_name ?? bet.gameName,
            userName: bet.username || "-",
            matchedBet: bet.selection || bet.player_name || "-",
            selection: bet.selection || "",
            odds: bet.odds ?? "0",
            stake: bet.amount ?? "0",
            placeDate: bet.created_at ?? "-",
            gametype: bet.game_type === "lay" ? "Lay" : "Back",
            type: bet.game_type || null,
          }))
        );
      }
    } catch (error) {
      console.error("Error fetching my bets (open-downline):", error);
    }
  }, [gameName]);

  useEffect(() => {
    if (!matchId) return undefined;
    fetchExposure();
    const id = setInterval(fetchExposure, 2000);
    return () => clearInterval(id);
  }, [matchId, fetchExposure]);

  useEffect(() => {
    fetchMyBetsData();
    const id = setInterval(fetchMyBetsData, 3000);
    return () => clearInterval(id);
  }, [fetchMyBetsData]);

  const game = GAMES[gameName] || GAMES.cmatch20;
  const title = game.title || fallbackTitle;
  const roundId = raw?.mid ?? "";
  const { Table } = game;

  const titleNode = game.videoTitle ? (
    <div className="casino-video-title">
      <span className="casino-name">{title}</span> <div className="casino-video-rid">Round ID: {roundId}</div>
    </div>
  ) : undefined;

  return (
    <CasinoTableLayout
      tableClass={game.tableClass}
      rightSide={<MyBet bets={myBets} onViewMore={() => setShowViewMoreModal(true)} />}
    >
      <>
        {!game.videoTitle && <CasinoHeading name={title} roundId={roundId} />}
        <VideoBox
          gameType={gameName}
          titleNode={titleNode}
          iframeSrc={`https://stream-s-43.uhdmovies.online/casino-stream?id=${gameName}`}
          timerValue={raw?.lt}
          cardsOverlay={game.overlay(raw?.card)}
        />
        {game.showDetail(raw) ? (
          <div className="casino-detail">
            <Table raw={raw} exposures={exposures} />{" "}
            <CasinoLastResult
              gameType={gameName}
              viewAllLink={`/admin/reports/casinoresult/${gameName}`}
              resultComponent={
                <UserSiteLastResult gameType={gameName} renderItem={game.item} Body={game.Body} fallbackTitle={title} />
              }
            />
          </div>
        ) : null}
      </>
      <ViewMoreModal
        show={showViewMoreModal}
        onHide={() => setShowViewMoreModal(false)}
        title="View More"
        sectionKey={gameName}
      />
    </CasinoTableLayout>
  );
}
